import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import PageLoader from "../PageLoader/PageLoaderComponent";
import Seo from "../Seo/SeoComponent";
import { useAuth } from "../../context/AuthContext";
import { useUserXp } from "../../hooks/useUserXp";
import { FEATURE_DISCOVERIES, isFeatureUnlocked } from "../../utils/featureDiscovery";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { isDesktopLocalProfile } from "../../utils/desktopProfile";
import { languageForPath } from "../../utils/documentLanguage";
import DesktopUnavailable from "../DesktopUnavailable";
import styles from "./featureGate.module.scss";

// The routes behind this gate are listed in the sitemap, but a signed-out visitor or a crawler only ever sees the
// gate card, which would leave the static index.html head in place (canonical "/", no hreflang: the page looks
// like a duplicate of the home page). So the gate card sets the same title, description, canonical and hreflang
// as the unlocked page's own Seo, from the same translation keys. A feature without an entry here sets none.
const GATE_SEO: Record<string, { namespace: string; path: string }> = {
    "kanji-memory-quiz": { namespace: "kanjiMemoryQuizPage", path: "/kanji-memory-quiz" },
    speaking: { namespace: "speakingPracticePage", path: "/speaking-practice" },
};

type FeatureGateProps = {
    featureId: string;
    children: ReactNode;
};

export default function FeatureGate({ featureId, children }: FeatureGateProps) {
    const { status: authStatus } = useAuth();
    const { status: xpStatus, progress, hasProgress, refresh } = useUserXp();
    const { t } = useTranslation("common");
    const gateSeo = GATE_SEO[featureId];
    const { t: tSeo } = useTranslation(gateSeo?.namespace ?? "common");
    const location = useLocation();
    const lp = useLocalizedPath();
    const feature = FEATURE_DISCOVERIES.find((item) => item.id === featureId);

    // Offline desktop build: there is no account to level up, so the quiz is simply open, and Speaking
    // Practice says it needs the network (speech recognition cannot run inside Electron) instead of opening a broken tool.
    if (isDesktopLocalProfile) return featureId === "speaking" ? <DesktopUnavailable feature="speaking" /> : <>{children}</>;

    if (!feature || !feature.requiresLevel || (hasProgress && isFeatureUnlocked(feature, progress.level))) return <>{children}</>;
    if (authStatus === "loading" || (authStatus === "authenticated" && !hasProgress && xpStatus === "loading")) return <PageLoader />;

    const loginPath = location.pathname.startsWith("/pt/") ? "/pt/login" : "/login";
    const title = t(`achievementDialog.features.${feature.id}.title`, { defaultValue: feature.title });

    const isPt = languageForPath(location.pathname) === "pt-PT";

    return <div className={styles.page}>
        {gateSeo && (
            <Seo
                title={tSeo("seoTitle")}
                description={tSeo("seoDescription")}
                path={isPt ? `/pt${gateSeo.path}` : gateSeo.path}
                lang={isPt ? "pt-PT" : undefined}
            />
        )}
        <Header />
        <main className={styles.main}>
            <section className={styles.card} aria-labelledby="feature-gate-title">
                <p className={styles.eyebrow}>{t("featureGate.optional")}</p>
                <h1 id="feature-gate-title">{title}</h1>
                <p>{t("featureGate.levelRequired", { level: feature.level })}</p>
                {authStatus !== "authenticated"
                    ? <Link className={styles.primary} to={loginPath} state={{ from: location }}>{t("featureGate.signIn")}</Link>
                    : xpStatus === "error"
                        ? <button className={styles.primary} type="button" onClick={refresh}>{t("featureGate.retry")}</button>
                        : <>
                            <p className={styles.progress}>{t("featureGate.currentLevel", { level: progress.level, remaining: Math.max(0, feature.level - progress.level) })}</p>
                            <Link className={styles.primary} to={lp("/profile")}>{t("featureGate.viewProgress")}</Link>
                        </>}
                <Link className={styles.secondary} to={lp("/lessons")}>{t("featureGate.continueLearning")}</Link>
            </section>
        </main>
        <Footer />
    </div>;
}
